import type { ReactNode } from "react";

/**
 * A section inside a wide overlay: heading on the left, an optional action on
 * the right, rows beneath.
 */
export function OverlaySection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-border border-b px-6 py-5 last:border-b-0">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-foreground text-base font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Two equal columns, both left-aligned: labels form one edge and values
 * another, so a section reads down either column. Only for wide surfaces —
 * the sidebar has no room for it.
 *
 * An absent value renders a muted "None" rather than an empty cell, so a
 * missing field is distinguishable from a rendering gap.
 */
export function OverlayRow({
  label,
  children,
}: {
  label: string;
  children?: ReactNode;
}) {
  const empty = children === null || children === undefined || children === "";

  return (
    <div className="grid grid-cols-2 items-start gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <div
        className={`min-w-0 break-words ${empty ? "text-subtle-foreground" : "text-foreground"}`}
      >
        {empty ? "None" : children}
      </div>
    </div>
  );
}
